import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Wrench, Search, Zap, MoreVertical, Pencil, Copy, Trash2,
  Globe, FolderOpen, EyeOff,
} from 'lucide-react';
import type { Agent } from '../hooks/useAgents';
import type { Instance } from '../types/instance';

// --- Role config ---

export const ROLE_CONFIG = {
  creator: { icon: Wrench, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
  executor: { icon: Zap, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700' },
  researcher: { icon: Search, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700' },
} as const;

// eslint-disable-next-line react-refresh/only-export-components
export function getRoleConfig(role: string) {
  return ROLE_CONFIG[role as keyof typeof ROLE_CONFIG] || ROLE_CONFIG.executor;
}

// --- Stats helpers ---

// eslint-disable-next-line react-refresh/only-export-components
export function computeAgentStats(agentId: string, instances: Instance[] | undefined) {
  if (!instances) return { count: 0, lastUsed: null as string | null, totalCost: 0 };
  const agentInstances = instances.filter(i => i.agentId === agentId);
  const count = agentInstances.length;
  const lastUsed = agentInstances.length > 0
    ? agentInstances.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0].createdAt
    : null;
  const totalCost = agentInstances.reduce((sum, i) => sum + Number(i.totalCostUsd), 0);
  return { count, lastUsed, totalCost };
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// --- Context menu ---

function ContextMenu({
  onEdit,
  onClone,
  onDelete,
  onDismiss,
  isSystem,
}: {
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  onDismiss?: () => void;
  isSystem?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-10 w-36 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {!isSystem && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onEdit(); }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          )}
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onClone(); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            <Copy className="h-3.5 w-3.5" /> Clone
          </button>
          <div className="my-1 border-t border-slate-100" />
          {isSystem ? (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onDismiss?.(); }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-50"
            >
              <EyeOff className="h-3.5 w-3.5" /> Dismiss
            </button>
          ) : (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onDelete(); }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// --- Shared props ---

export interface AgentCardProps {
  agent: Agent;
  instances?: Instance[];
  onEdit: (agent: Agent) => void;
  onClone: (agent: Agent) => void;
  onDelete: (agent: Agent) => void;
  onDismiss?: (agent: Agent) => void;
  onRestore?: (agent: Agent) => void;
}

// --- Grid card ---

export function AgentCardGrid({ agent, instances, onEdit, onClone, onDelete, onDismiss }: AgentCardProps) {
  const cfg = getRoleConfig(agent.role);
  const Icon = cfg.icon;
  const stats = computeAgentStats(agent.id, instances);
  const ScopeIcon = agent.scope === 'global' ? Globe : FolderOpen;

  return (
    <Link
      to={`/agents/${agent.id}`}
      className={`group flex flex-col rounded-xl border bg-white p-5 transition-all hover:shadow-md ${cfg.border}`}
    >
      <div className="flex items-start justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${cfg.bg}`}>
          <Icon className={`h-5 w-5 ${cfg.color}`} />
        </div>
        <ContextMenu
          onEdit={() => onEdit(agent)}
          onClone={() => onClone(agent)}
          onDelete={() => onDelete(agent)}
          onDismiss={() => onDismiss?.(agent)}
          isSystem={agent.isSystem}
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <h3 className="font-semibold text-slate-900">{agent.name}</h3>
        {agent.isSystem && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">System</span>
        )}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.badge}`}>
          {agent.role}
        </span>
        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
          <ScopeIcon className="h-3 w-3" />
          {agent.scope}
        </span>
      </div>

      {agent.defaultModel && (
        <p className="mt-2 truncate text-xs text-slate-400">{agent.defaultModel}</p>
      )}

      <div className="mt-auto border-t border-slate-100 pt-3 text-xs text-slate-400" style={{ marginTop: 'auto', paddingTop: '0.75rem' }}>
        <div className="flex items-center gap-4">
          <span>{stats.count} instance{stats.count !== 1 ? 's' : ''}</span>
          {stats.totalCost > 0 && <span>${stats.totalCost.toFixed(2)}</span>}
          {stats.lastUsed && <span className="ml-auto">{relativeTime(stats.lastUsed)}</span>}
        </div>
      </div>
    </Link>
  );
}

// --- List row ---

export function AgentCardList({ agent, instances, onEdit, onClone, onDelete, onDismiss }: AgentCardProps) {
  const cfg = getRoleConfig(agent.role);
  const Icon = cfg.icon;
  const stats = computeAgentStats(agent.id, instances);
  const ScopeIcon = agent.scope === 'global' ? Globe : FolderOpen;

  return (
    <Link
      to={`/agents/${agent.id}`}
      className={`flex items-center gap-4 rounded-lg border bg-white px-4 py-3 transition-all hover:shadow-sm ${cfg.border}`}
    >
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${cfg.bg}`}>
        <Icon className={`h-4 w-4 ${cfg.color}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-slate-900">{agent.name}</span>
          {agent.isSystem && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">System</span>
          )}
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.badge}`}>
            {agent.role}
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-slate-400">
            <ScopeIcon className="h-3 w-3" />
          </span>
        </div>
        {agent.defaultModel && (
          <p className="mt-0.5 truncate text-xs text-slate-400">{agent.defaultModel}</p>
        )}
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-400">
        <span>{stats.count} runs</span>
        {stats.totalCost > 0 && <span>${stats.totalCost.toFixed(2)}</span>}
        {stats.lastUsed && <span>{relativeTime(stats.lastUsed)}</span>}
      </div>
      <ContextMenu
        onEdit={() => onEdit(agent)}
        onClone={() => onClone(agent)}
        onDelete={() => onDelete(agent)}
        onDismiss={() => onDismiss?.(agent)}
        isSystem={agent.isSystem}
      />
    </Link>
  );
}

// --- Default export for backward compat ---

export default function AgentCard({ agent, onEdit }: { agent: Agent; onEdit: (agent: Agent) => void }) {
  return (
    <AgentCardGrid
      agent={agent}
      onEdit={onEdit}
      onClone={() => {}}
      onDelete={() => {}}
    />
  );
}
